import React, { useCallback, useEffect, useState } from 'react';
import platformApi, { platformErrorMessage, PLATFORM_API_URL } from '../services/platformApi';
import { formatDate } from '../utils/billingAdmin';

function statusBadge(item) {
  if (item.ok) return <span className="badge badge--active">Done</span>;
  if (!item.required) return <span className="badge badge--pending">Recommended</span>;
  return <span className="badge badge--failed">Missing</span>;
}

/**
 * Everything needed before taking real payments, checked live against the platform backend.
 * @param {{ refreshKey?: unknown }} props - change it to re-run the check (e.g. after saving settings)
 */
function GoLiveChecklist({ refreshKey }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    platformApi.get('/platform/go-live')
      .then((response) => setData(response.data?.data || null))
      .catch((err) => setError(platformErrorMessage(err, 'Failed to load the go-live checklist')))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load, refreshKey]);

  const webhook = data?.webhook;
  const seen = webhook?.seen || {};

  return (
    <div className="card" style={{ marginBottom: '1.5rem' }}>
      <div className="card__header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1rem' }}>
        <h2 className="card__title">
          Go-live checklist{' '}
          {data && (data.ready
            ? <span className="badge badge--active">Ready for live payments</span>
            : <span className="badge badge--failed">Not ready</span>)}
        </h2>
        <button type="button" className="btn btn--secondary btn--sm" onClick={load} disabled={loading}>
          {loading ? 'Checking…' : 'Check again'}
        </button>
      </div>
      <div className="card__body">
        {error && <div className="alert alert--error">{error}</div>}
        {data && (
          <>
            <div className="table-wrapper">
              <table className="data-table data-table--responsive">
                <thead>
                  <tr><th>Check</th><th>Status</th><th>Details</th></tr>
                </thead>
                <tbody>
                  {data.checks.map((item) => (
                    <tr key={item.key}>
                      <td data-label="Check">{item.label}</td>
                      <td data-label="Status">{statusBadge(item)}</td>
                      <td data-label="Details">{item.detail || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {webhook && (
              <div style={{ marginTop: '1.25rem' }}>
                <p className="form-label">Razorpay webhook</p>
                <p className="form-hint">
                  URL: <span className="cell-mono">{`${PLATFORM_API_URL}${webhook.path}`}</span>
                </p>
                <p className="form-hint">
                  Enable these events in the Razorpay dashboard. Events received in the last {webhook.lookbackDays} days are marked; an
                  event that hasn&apos;t happened yet (for example a dispute) is not necessarily misconfigured.
                </p>
                <div className="btn-group" style={{ flexWrap: 'wrap', gap: '0.5rem' }}>
                  {webhook.expectedEvents.map((event) => (
                    <span
                      key={event}
                      className={`badge ${seen[event] ? 'badge--active' : 'badge--inactive'}`}
                      title={seen[event] ? `${seen[event].count} received, last ${formatDate(seen[event].lastReceivedAt, { withTime: true })}` : 'Not received recently'}
                    >
                      {event}{seen[event] ? ` · ${seen[event].count}` : ''}
                    </span>
                  ))}
                </div>
              </div>
            )}
            <p className="form-hint" style={{ marginTop: '1rem' }}>
              Keys, secrets and SMTP are managed under Payment Providers and Email Config. Checked {formatDate(data.checkedAt, { withTime: true })}.
            </p>
          </>
        )}
      </div>
    </div>
  );
}

export default GoLiveChecklist;
